import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangContext } from "@/lib/i18n";
import { Toaster } from "sonner";

import Layout from "@/components/app/Layout";
import ImpersonationBanner from "@/components/app/ImpersonationBanner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import DashboardToday from "@/pages/DashboardToday";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import NewOrder from "@/pages/NewOrder";
import Products from "@/pages/Products";
import Suppliers from "@/pages/Suppliers";
import Inventory from "@/pages/Inventory";
import Restaurants from "@/pages/Restaurants";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import Invoices from "@/pages/Invoices";
import Documents from "@/pages/Documents";
import InvoiceCheck from "@/pages/InvoiceCheck";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import PlatformDashboard from "@/pages/PlatformDashboard";
import AutoFillSettings from "@/pages/AutoFillSettings";
import ModulesSettings from "@/pages/ModulesSettings";
import ShiftRegistratie from "@/pages/ShiftRegistratie";
import SupportDashboard from "@/pages/SupportDashboard";
import CyberDashboard from "@/pages/CyberDashboard";
import HaccpTraining from "@/pages/HaccpTraining";
import PlatformHaccp from "@/pages/PlatformHaccp";

function Protected({ children, adminOnly, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500" data-testid="auth-loading">Laden…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !["admin", "organization_admin", "manager", "super_admin", "platform_owner"].includes(user.role)) return <Navigate to="/" replace />;
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    // platform_owner bypasses all role checks
    if (user.role !== "platform_owner" && !allowed.includes(user.role)) return <Navigate to="/" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === "super_admin" || user.role === "platform_owner") return <Navigate to="/platform" replace />;
    if (user.role === "support") return <Navigate to="/support" replace />;
    if (user.role === "cybersecurity") return <Navigate to="/cyber" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function RoleRouter({ children }) {
  // Org users (admin/chef) use the main Layout-wrapped app.
  // Platform roles get redirected away from /
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin" || user.role === "platform_owner") return <Navigate to="/platform" replace />;
  if (user.role === "support") return <Navigate to="/support" replace />;
  if (user.role === "cybersecurity") return <Navigate to="/cyber" replace />;
  return children;
}

function LangShell({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("stockly_lang") || "nl");
  useEffect(() => {
    if (user && user.language && user.language !== lang) {
      setLang(user.language);
    }
  }, [user, lang]);
  useEffect(() => { localStorage.setItem("stockly_lang", lang); }, [lang]);
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

function AppLayout() {
  return (
    <>
      <ImpersonationBanner />
      <Layout />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LangShell>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/wachtwoord-vergeten" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/wachtwoord-reset" element={<PublicOnly><ResetPassword /></PublicOnly>} />
            <Route path="/platform" element={<Protected role={["super_admin", "platform_owner"]}><><ImpersonationBanner /><PlatformDashboard /></></Protected>} />
            <Route path="/platform/auto-fill" element={<Protected role={["super_admin", "platform_owner"]}><><ImpersonationBanner /><AutoFillSettings /></></Protected>} />
            <Route path="/platform/modules" element={<Protected role={["super_admin", "platform_owner"]}><><ImpersonationBanner /><ModulesSettings /></></Protected>} />
            <Route path="/platform/haccp" element={<Protected role={["super_admin", "platform_owner"]}><><ImpersonationBanner /><PlatformHaccp /></></Protected>} />
            <Route path="/support" element={<Protected role={["support", "platform_owner"]}><><ImpersonationBanner /><SupportDashboard /></></Protected>} />
            <Route path="/cyber" element={<Protected role={["cybersecurity", "platform_owner"]}><><ImpersonationBanner /><CyberDashboard /></></Protected>} />
            <Route element={<Protected><RoleRouter><AppLayout /></RoleRouter></Protected>}>
              <Route path="/" element={<DashboardToday />} />
              <Route path="/dashboard-preview" element={<Navigate to="/" replace />} />
              <Route path="/dashboard-klassiek" element={<Dashboard />} />
              <Route path="/bestellingen" element={<Orders />} />
              <Route path="/bestellingen/:id" element={<OrderDetail />} />
              <Route path="/nieuwe-bestelling" element={<NewOrder />} />
              <Route path="/producten" element={<Products />} />
              <Route path="/leveranciers" element={<Protected adminOnly><Suppliers /></Protected>} />
              <Route path="/voorraad" element={<Inventory />} />
              <Route path="/restaurants" element={<Protected adminOnly><Restaurants /></Protected>} />
              <Route path="/instellingen" element={<Settings />} />
              <Route path="/profiel" element={<Profile />} />
              <Route path="/facturen" element={<Invoices />} />
              <Route path="/documenten" element={<Documents />} />
              <Route path="/factuurcontrole" element={<InvoiceCheck />} />
              <Route path="/team" element={<Protected adminOnly><Team /></Protected>} />
              <Route path="/shiftregistratie" element={<Protected adminOnly><ShiftRegistratie /></Protected>} />
              <Route path="/training/haccp" element={<HaccpTraining />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </LangShell>
    </AuthProvider>
  );
}
